import omit from 'lodash.omit';

/**
 * Creates a frame fitted for the image given based off the frame and bounds provided
 *
 * @param {Object} cardBounds The card bounds, including a width & height
 * @param {Object} elementFrame The element frame, including a width & height
 * @param {Object} assetImage The asset image to use, including a width & height
 * @param {Boolean} centeredX Whether to center the element in the X axis or not
 * @param {Boolean} centeredY Whether to center the element in the Y axis or not
 *
 * @returns {Object} the generated frame based off the given items
 */
export const getImageBasedResizedFrame = (
  cardBounds,
  elementFrame,
  assetImage,
  centeredX = false,
  centeredY = false,
) => {
  const { width, height } = assetImage ?? {};
  const { width: cardWidth, height: cardHeight } = cardBounds ?? {};
  const { width: frameWidth, height: frameHeight } = elementFrame ?? {};

  if (!width || !height) {
    return elementFrame;
  }

  if (!cardWidth || !cardHeight) {
    return elementFrame;
  }

  if (!frameWidth || !frameHeight) {
    return elementFrame;
  }

  const isLandscape = width >= height;
  const cardIsLandscape = cardWidth >= cardHeight;
  const ratio = Math.max(width, height) / Math.min(width, height);
  const cardRatio =
    Math.max(cardWidth, cardHeight) / Math.min(cardWidth, cardHeight);

  let finalWidth = frameWidth;
  let finalHeight = frameHeight;

  // We will reduce enough so the original value can remain the same
  // while we maintain an aspect ratio appropriate to the given image.
  // We choose to reduce instead of increase since this will work 100%
  // of the time regardless of where the photo is positioned - as a
  // photo will never outgrow the card nor will it ever go off limits
  // when it is sized down.
  const minDimension = Math.min(frameWidth, frameHeight);
  if (frameWidth === frameHeight) {
    finalWidth = !cardIsLandscape ? minDimension : minDimension / cardRatio;
    finalHeight = cardIsLandscape ? minDimension : minDimension / cardRatio;
  } else if (
    (minDimension === frameWidth && cardIsLandscape) ||
    (minDimension === frameHeight && !cardIsLandscape)
  ) {
    finalWidth =
      minDimension === frameWidth ? minDimension : minDimension * cardRatio;
    finalHeight =
      minDimension === frameHeight ? minDimension : minDimension * cardRatio;
  } else {
    finalWidth =
      minDimension === frameWidth ? minDimension : minDimension / cardRatio;
    finalHeight =
      minDimension === frameHeight ? minDimension : minDimension / cardRatio;
  }

  if (isLandscape && ratio !== 1) {
    finalHeight /= ratio;
  } else if (!isLandscape && ratio !== 1) {
    finalWidth /= ratio;
  }

  return {
    ...omit(elementFrame, ['__typename']),
    width: Math.round(finalWidth * 1e2) / 1e2,
    height: Math.round(finalHeight * 1e2) / 1e2,
    x: centeredX
      ? Math.round(((1 - finalWidth) / 2) * 1e2) / 1e2
      : elementFrame.x,
    y: centeredY
      ? Math.round(((1 - finalHeight) / 2) * 1e2) / 1e2
      : elementFrame.y,
  };
};
